import axios from "axios";
import { DATA_SVC_RS_ADDRESS } from "constants/app.constants";
import type { SortByT } from "store/features/filter/filterSlice";
import { useOthersMembersAvailabilityStore } from "stores/othersMembersAvailabilityStore";
import * as z from "zod";

import { mBinSize } from "models/binSize";
import { EntityKind } from "models/entityKind";
import { INormalizeBySetting } from "models/normalizeBy";
import { PdenSourceEnum } from "models/pdenDataSourceSetting";

export const BIN_SEARCH_ENDPOINT = `${DATA_SVC_RS_ADDRESS}/api/v1/data/bin-search`;
export const OTHERS_MEMBERS_PAGE_SIZE = 50;
// data-svc-rs rejects a longer search with a 400; it counts characters, not UTF-16 units.
export const OTHERS_MEMBERS_MAX_SEARCH_LENGTH = 200;
// Each search term is its own query key, so a shorter wait sends (and aborts) a
// request per keystroke at a normal typing pace.
export const BIN_SEARCH_DEBOUNCE_MS = 500;

export function isSearchWithinLimit(search: string): boolean {
  return [...search].length <= OTHERS_MEMBERS_MAX_SEARCH_LENGTH;
}

export function statusOf(error: unknown): Maybe<number> {
  return (error as { response?: { status?: number } })?.response?.status;
}

// Only a 404 means the route isn't deployed here; remembering it stops every other
// mounted control from re-probing it this session.
export function markUnavailableOn404(error: unknown): void {
  if (statusOf(error) === 404) {
    useOthersMembersAvailabilityStore.getState().markUnavailable();
  }
}

// The request must match the map's GroupBy, which embeds the focus's own pdenSource,
// not the app-level default setting.
export function toGroupByPdenSource(pdenSource: string): PdenSourceEnum {
  return typeof pdenSource === "string"
    ? PdenSourceEnum[pdenSource]
    : parseInt(pdenSource);
}

export type BinSearchView = "all" | "legend" | "others";

export interface OthersMembersRequest {
  filterId: string;
  property: string;
  bin: mBinSize;
  entityKind?: EntityKind;
  pdenSource?: number;
  normalizeBySetting?: Nullable<INormalizeBySetting>;
  showGroupsNotInFilter?: boolean;
  shapefileMatchMode?: Nullable<string>;
  // The legend's sort, so the paged list comes back in the legend's order.
  sortBy?: SortByT;
  search?: Nullable<string>;
  offset?: number;
  limit?: number;
  view?: BinSearchView;
}

const OthersMemberSchema = z.object({
  title: z.string(),
  count: z.number(),
  placement: z.enum(["legend", "others"])
});

const OthersMembersResponseSchema = z.object({
  members: z.array(OthersMemberSchema),
  total: z.number(),
  truncated: z.boolean()
});

export type OthersMember = z.infer<typeof OthersMemberSchema>;
export type OthersMembersResponse = z.infer<typeof OthersMembersResponseSchema>;

/**
 * Never goes through `sanitizeBinForRequest` (api/data.ts), which maps `""`
 * to 0: bin-search needs the bounds' `""` sentinel mapped to `null`, and
 * reads `max_bins: Option<i32>`, so a non-positive MaxBins goes out as null.
 *
 * Audited against `data-svc-rs`'s `Bin` struct (models/request.rs):
 * - `BinSize` is `f64` with `#[serde(default)]`, which only covers an ABSENT
 *   key — every real bin here already carries the key (e.g. the "" from a
 *   Text field's default bin), and serde errors deserializing "" into f64,
 *   422-ing every such request. Mapped to 0 like `sanitizeBinForRequest`.
 * - `MaxBinsSortOrder` has no `#[serde(default)]`, so the key must be
 *   present even when `undefined` (JSON.stringify would otherwise drop it
 *   entirely) — defaulted to `null` here.
 * - `LessThan`/`GreaterThan`/`MinSize` are already always-present keys on
 *   `mBinSize`, so the `""` -> `null` mapping below is sufficient for them.
 *
 * Bin fields can arrive as strings from BinSettings; converted with
 * `Number()`, like `sanitizeBinForRequest` (api/data.ts).
 */
export function toBinSearchBin(bin: mBinSize): mBinSize {
  const toNumber = (v: Maybe<number | string>): Nullable<number> => {
    if (v === "" || v === null || v === undefined) return null;
    const n = typeof v === "string" ? Number(v) : v;
    return Number.isFinite(n) ? n : null;
  };
  const maxBins = Math.trunc(toNumber(bin.MaxBins) ?? 0);

  return {
    ...bin,
    BinSize: toNumber(bin.BinSize) ?? 0,
    LessThan: toNumber(bin.LessThan),
    GreaterThan: toNumber(bin.GreaterThan),
    MinSize: toNumber(bin.MinSize) as number | "",
    MaxBins: (maxBins > 0 ? maxBins : null) as number | "",
    BinType: bin.BinType === "Dynamic" ? "BinSize" : bin.BinType,
    MaxBinsSortOrder: bin.MaxBinsSortOrder ?? null,
    // Not read by bin-search; left in, the auto-lock on a first check reads as a new request.
    IsLocked: undefined
  };
}

export async function fetchBinSearch(
  body: OthersMembersRequest,
  signal?: AbortSignal
): Promise<OthersMembersResponse> {
  const response = await axios.post(BIN_SEARCH_ENDPOINT, body, { signal });
  const result = OthersMembersResponseSchema.safeParse(response.data);
  if (!result.success) {
    throw new Error(
      `Unexpected bin-search response shape from ${BIN_SEARCH_ENDPOINT}: ${result.error.message}`
    );
  }
  return result.data;
}
